import React, { ChangeEvent, FC, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICustomSurveyQuestion } from '../../../shared/interfaces/ICustomSurveyQuestion';
import Accordion from '../../../components/Accordion/Accordion';
import { AccordionSummaryContentBox } from '../../../components/Survey/Survey';
import { Add, Delete, Save } from '@mui/icons-material';
import SurveyService from '../../../services/SurveyService';
import { ISurveysEntry } from '../../../shared/interfaces/ISurveysEntry';
import Chip from '../../../components/Chip/Chip';
import Typography from '@mui/material/Typography';
import { ICustomSurveyQuestionOption } from '../../../shared/interfaces/ICustomSurveyQuestionOption';

interface Props {
  question: ICustomSurveyQuestion;
  survey: ISurveysEntry;
  reorderAndReload: (qid: string | undefined) => Promise<void>;
  reload: (qid: string | undefined) => Promise<void>;
}

const SurveyQuestion: FC<Props> = ({ survey, question, reload, reorderAndReload }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [current, setCurrent] = useState<ICustomSurveyQuestion>(question);

  const deleteQuestion = async () => {
    await SurveyService.deleteQuestion(survey.type, survey.id, question.questionId || '');
    await reorderAndReload(question.questionId);
  };

  const addOption = async () => {
    const options = current.options || [];
    setCurrent({
      ...current,
      options: options.concat([{ label: 'Neu ' + (options.length + 1) }]),
    });
  };

  const removeOption = async (index: number) => {
    const oldOptions = current.options || [];

    let options = [];
    for (let i = 0; i < oldOptions.length; i++) {
      if (i != index) {
        options.push(oldOptions[i]);
      }
    }
    setCurrent({
      ...current,
      options: options,
    });
  };

  const handleOptional = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrent({
      ...current,
      optional: event.target.checked,
    });
  };

  const getQuestionTypeName = (type: string) => {
    switch (type) {
      case 'CUSTOMSINGLE':
        return t('survey_question_customsingle');
      case 'CUSTOMTEXT':
        return t('survey_question_customtext');
      case 'CUSTOMYESNO':
        return t('survey_question_customyesno');
      case 'CUSTOMSCALENPS':
        return t('survey_question_customscalenps');
      case 'CUSTOMDROPDOWN':
        return t('survey_question_customdropdown');
      default:
        return type;
    }
  };

  const renderOptions = () => {
    let options = current.options || [];

    return (
      <>
        {options.map((option: ICustomSurveyQuestionOption, index: number) => {
          return (
            <Box
              key={index + '.' + option.label}
              sx={{
                gap: theme.spacing(1),
                mb: theme.spacing(1),
                display: 'flex',
                alignItems: 'stretch',
              }}
            >
              <TextField
                fullWidth
                size="small"
                defaultValue={option.label}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  (option.label = event.target.value)
                }
              />
              <Button
                variant="contained"
                startIcon={<Delete />}
                onClick={() => removeOption(index)}
              />
            </Box>
          );
        })}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addOption}
        />
      </>
    );
  };

  const renderNpsOptions = () => {
    if (!current.options || current.options.length != 2) {
      setCurrent({
        ...current,
        options: [{ label: 'Sehr unwahrscheinlich' }, { label: 'Sehr wahrscheinlich' }],
      });
      return null;
    }

    return (
      <>
        <Box
          sx={{
            gap: theme.spacing(1),
            mb: theme.spacing(1),
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <TextField
            fullWidth
            label={t('scale0_label')}
            size="small"
            defaultValue={current.options[0].label}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              (current.options[0].label = event.target.value)
            }
          />
        </Box>
        <Box
          sx={{
            gap: theme.spacing(1),
            mb: theme.spacing(1),
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <TextField
            fullWidth
            label={t('scale10_label')}
            size="small"
            defaultValue={current.options[1].label}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              (current.options[1].label = event.target.value)
            }
          />
        </Box>
      </>
    );
  };

  const saveQuestion = async () => {
    await SurveyService.updateQuestion(survey.type, survey.id, question.questionId || '', {
      ...current,
      position: question.position,
    });
    await reload('');
    setIsExpanded(false);
  };

  const typeName = getQuestionTypeName(question.type);

  return (
    <Accordion
      expanded={isExpanded}
      onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
      summary={
        <AccordionSummaryContentBox>
          {question.position}. {question.question}
          {typeName && (
            <Chip
              size="small"
              label={typeName}
              sx={{ ml: theme.spacing(2) }}
            />
          )}
        </AccordionSummaryContentBox>
      }
      details={
        <>
          <TextField
            fullWidth
            size="small"
            defaultValue={current.question}
            label={t('question_text')}
            sx={{ mb: theme.spacing(2) }}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              (current.question = event.target.value)
            }
          />
          {question.type == 'CUSTOMTEXT' && (
            <>
              <TextField
                fullWidth
                size="small"
                defaultValue={current.placeholder}
                label={t('question_placeholder')}
                sx={{ mb: theme.spacing(2) }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  (current.placeholder = event.target.value)
                }
              />
            </>
          )}
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={current.optional}
                  onChange={handleOptional}
                />
              }
              label={t('question_optional')}
              sx={{ mb: theme.spacing(2), ml: theme.spacing(0) }}
            />
          </FormGroup>
          {(question.type == 'CUSTOMSINGLE' || question.type == 'CUSTOMDROPDOWN') && (
            <>
              <Typography>{t('question_options')}</Typography>
              <Box sx={{ mb: theme.spacing(2) }}>{renderOptions()}</Box>
            </>
          )}
          {question.type == 'CUSTOMSCALENPS' && (
            <>
              <Box sx={{ mb: theme.spacing(2) }}>{renderNpsOptions()}</Box>
            </>
          )}

          <Button
            variant="contained"
            startIcon={<Save />}
            sx={{ mr: theme.spacing(1) }}
            onClick={saveQuestion}
          >
            {t('save')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Delete />}
            sx={{ mr: theme.spacing(1) }}
            onClick={deleteQuestion}
          >
            {t('delete_question')}
          </Button>
        </>
      }
    />
  );
};

export default SurveyQuestion;
